import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Typography from "@material-ui/core/Typography";
import api from "../../services/api";
import { toast } from "react-toastify";
import { useTheme } from "@material-ui/core/styles";
import {
  LOW_VOLUME_COLOR,
  MID_VOLUME_COLOR,
  HIGH_VOLUME_COLOR,
  buildVolumeLegend,
  getChartTextColor,
  getChartGridColor,
  translateWeekDayShort
} from "./chartTheme";
import { i18n } from "../../translate/i18n";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const buildColorsByTercile = values => {
  if (!values.length) {
    return [];
  }

  const sortedValues = [...values].sort((a, b) => a - b);
  const pickQuantile = quantile => {
    const index = Math.floor((sortedValues.length - 1) * quantile);
    return sortedValues[index];
  };

  const firstTercile = pickQuantile(1 / 3);
  const secondTercile = pickQuantile(2 / 3);

  return values.map(value => {
    if (firstTercile === secondTercile) {
      if (value < firstTercile) {
        return LOW_VOLUME_COLOR;
      }
      if (value > secondTercile) {
        return HIGH_VOLUME_COLOR;
      }
      return MID_VOLUME_COLOR;
    }

    if (value <= firstTercile) {
      return LOW_VOLUME_COLOR;
    }

    if (value <= secondTercile) {
      return MID_VOLUME_COLOR;
    }

    return HIGH_VOLUME_COLOR;
  });
};

const buildOptions = (textColor, gridColor) => ({
  responsive: true,
  scales: {
    x: {
      ticks: {
        autoSkip: true,
        maxTicksLimit: 16,
        maxRotation: 0,
        minRotation: 0,
        color: textColor,
        font: {
          size: 10
        }
      },
      grid: {
        color: gridColor
      }
    },
    y: {
      beginAtZero: true,
      ticks: {
        color: textColor
      },
      grid: {
        color: gridColor
      }
    }
  },
  plugins: {
    legend: buildVolumeLegend(textColor),
    title: {
      display: true,
      text: i18n.t("dashboard.charts.ticketsPerDay.chartTitle"),
      position: "left",
      color: textColor
    },
    datalabels: {
      display: true,
      anchor: "center",
      align: "center",
      color: "#FFFFFF",
      font: {
        weight: "bold"
      },
      formatter: value => Math.trunc(Number(value) || 0)
    }
  }
});

export const ChartsPeakDays = ({ initialDate, finalDate }) => {
  const [ticketsData, setTicketsData] = useState({ data: [] });
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const textColor = getChartTextColor(isDark);
  const gridColor = getChartGridColor(isDark);

  useEffect(() => {
    handleGetPeakDaysInformation(initialDate, finalDate);
  }, [initialDate, finalDate]);

  // Autor: GitHub Copilot | Data: 23/04/2026 | Motivo: mostrar dia da semana junto com o dia do mes no eixo X.
  const dayLabels = ticketsData?.data?.map(item => {
    const dayOfWeek = translateWeekDayShort(item.diaSemana || "");
    return [dayOfWeek, String(item.dia)];
  }) || [];
  const dayValues = ticketsData?.data?.map(item => Math.trunc(Number(item.quantidade) || 0)) || [];

  const dataCharts = {
    labels: dayLabels,
    datasets: [
      {
        data: dayValues,
        backgroundColor: buildColorsByTercile(dayValues)
      }
    ]
  };

  const handleGetPeakDaysInformation = async (selectedInitialDate, selectedFinalDate) => {
    try {
      if (!selectedInitialDate || !selectedFinalDate) {
        return;
      }

      const { data } = await api.get("/dashboard/ticketsPeakDays", {
        params: {
          initialDate: selectedInitialDate,
          finalDate: selectedFinalDate
        }
      });

      setTicketsData(data);
    } catch (error) {
      toast.error(i18n.t("dashboard.charts.ticketsPerDay.loadError"));
    }
  };

  return (
    <>
      <Typography component="h2" variant="h6" color="primary" gutterBottom>
        {i18n.t("dashboard.charts.ticketsPerDay.title")}
      </Typography>

      <Bar options={buildOptions(textColor, gridColor)} data={dataCharts} style={{ maxWidth: "100%", maxHeight: "280px" }} />
    </>
  );
};